import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Chip,
    Button,
    Divider,
    CircularProgress,
    useMediaQuery
} from '@mui/material';

import DynamicTable from '../../components/table';
import DeletePopup from './delete';
import DeletePopupMobile from './mobile/delete-mobile';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import theme from '../../util/theme/theme';
import { useSettings } from '../../providers/settings-provider';

import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import useApi from '../../hooks/use-api';
import { useToast } from '../../providers/toaster-provider';
import Tooltip from '../ui/tooltip';
import { usePermissions } from '../../providers/permissions-provider';

interface Transaction {
    guid: string;
    description: string;
    amount: number;
    date: string;
    type: string;
    category: string;
    bankAccount: string;
    importId: number;
}

interface ImportSummaryData {
    importEvent: {
        id: number;
        entityId: number;
        entityName: string | null;
        moduleName: string;
        fileName: string;
        description: string | null;
        isReverted: boolean;
        result: {
            success: boolean;
            createdCount: number;
            totalProcessed: number;
            timestamp: string;
            message: string;
        } | null;
        createdAt: string;
        updatedAt: string;
    };
    transactions: Transaction[];
    summary: {
        totalTransactions: number;
        activeTransactions: number;
        linkedTransactions: number;
    };
}

interface ImportSummaryPopupProps {
    open: boolean;
    onClose: () => void;
    importId: string | null;
}

const ImportSummaryPopup: React.FC<ImportSummaryPopupProps> = ({ open, onClose, importId }) => {
    const { formatDate } = useSettings();
    const { showError, showSuccess } = useToast();
    // Same permission as the Revert/Restore row actions on the Imports page
    // (import-events.js checks it server-side too).
    const { can } = usePermissions();
    const canRevert = can('imports', 'delete');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const [importData, setImportData] = React.useState<ImportSummaryData | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

    const [fetchImportDetails, setFetchImportDetails] = useApi<ImportSummaryData>();
    const [deleteImport, setDeleteImport] = useApi();
    const [restoreImport, setRestoreImport] = useApi();

    const { data: fetchedData, isLoading: fetching, error: fetchError } = fetchImportDetails;

    React.useEffect(() => {
        if (!importId) return;
        const { url, method } = buildApiRequestConfig(routes.imports.fetchById, [{ param: 'id', value: importId }]);
        setFetchImportDetails({ url, method });
    }, [importId, deleteImport, restoreImport]);

    React.useEffect(() => {
        setImportData(null);
    }, [open]);

    React.useEffect(() => {
        if (fetchedData && !fetching) {
            setImportData(fetchedData);
        }
        if (fetchError) {
            showError("Failed to fetch import details.");
        }
    }, [fetchedData, fetching, fetchError]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteImport;
        if (data && !isLoading) {
            showSuccess("Import reverted successfully.");
        }
        if (error) {
            showError("Failed to revert import.");
        }
    }, [deleteImport]);

    React.useEffect(() => {
        const { data, isLoading, error } = restoreImport;
        if (data && !isLoading) {
            showSuccess("Import restored successfully.");
        }
        if (error) {
            showError("Failed to restore import.");
        }
    }, [restoreImport]);

    const onRevert = () => {
        if (importId && importEvent) {
            if (importEvent.isReverted) {
                // Restore the import
                const { url, method } = buildApiRequestConfig(routes.imports.restore, [{ param: 'id', value: importId }]);
                setRestoreImport({ url, method });
            } else {
                // Revert the import
                const { url, method } = buildApiRequestConfig(routes.imports.delete, [{ param: 'id', value: importId }]);
                setDeleteImport({ url, method });
            }
        }
    };

    const transactionColumns = [
        { 
            id: 'date', 
            label: 'Date', 
            width: 120, 
            type: 'date' as const,
        },
        { id: 'description', label: 'Description', width: 200, type: 'string' as const },
        { 
            id: 'amount', 
            label: 'Amount', 
            width: 120, 
            type: 'currency' as const,
        },
        { id: 'category', label: 'Category', width: 120, type: 'string' as const },
        { id: 'bankAccount', label: 'Account', width: 140, type: 'string' as const },
    ];

    const { importEvent, transactions, summary } = importData || { importEvent: null, transactions: [], summary: { totalTransactions: 0, activeTransactions: 0 } };

    const importSummaryContent = fetching ? (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                    Import Summary
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Result:</strong>
                    </Typography>
                    <Chip
                        label="Loading..."
                        color="default"
                        size="small"
                        sx={{ width: 100, borderRadius: 1 }}
                    />
                </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', mb: 1, backgroundColor: 'var(--color-container-background)', p: 2, borderRadius: 1, flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={30} sx={{ mb: 1 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
                    Loading Import Details...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Please wait while we fetch the import summary.
                </Typography>
            </Box>
        </>
    ) : (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 20 }}>
                    Import Summary
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Result:</strong>
                    </Typography>
                    <Chip
                        label={importEvent?.result ? (importEvent.result.success ? 'Success' : 'Failed') : 'Incomplete'}
                        color={importEvent?.result ? (importEvent.result.success ? 'success' : 'error') : 'default'}
                        size="small"
                        sx={{ width: 100, borderRadius: 1 }}
                    />
                </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 0.5, sm: 0 }, mb: 1, backgroundColor: 'var(--color-container-background)', p: 2, borderRadius: 1}}>
                <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {String(importEvent?.id).padStart(3, '0')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Date:</strong> {formatDate(importEvent ? new Date(importEvent?.createdAt) : new Date(), true)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Module:</strong> {importEvent?.moduleName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Files:</strong> <a href="#">{importEvent?.fileName}</a> | <a href="#">export_placeholder</a>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Total Transactions:</strong> {summary.totalTransactions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Active Transactions:</strong> {summary.activeTransactions}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <strong>Linked Transactions:</strong> <a href={`/linked-imports?importId=${importEvent?.id}`}>{summary.linkedTransactions}</a>
                </Typography>
            </Box>
        </>
    );

    const importSummaryFooter = (
        <>
            <Divider sx={{ my: 1 }} />
            <Box maxHeight={400} sx={{ overflowY: 'hidden' }}>
                {transactions.length > 0 ? (
                    <DynamicTable
                        columns={transactionColumns}
                        data={transactions}
                        contained={false}
                        maxHeight={400}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        <Typography variant="body1">
                            No active transactions found for this import.
                        </Typography>
                    </Box>
                )}
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderColor: 'var(--color-primary)',
                        bgcolor: 'var(--color-page-background)',
                        color: 'var(--color-primary)',
                        textTransform: 'none'
                    }}
                >
                    Close
                </Button>
                <Box display="flex" alignItems="center">
                    { !canRevert
                        ? <Tooltip message="You don't have permission to do this"/>
                        : (summary?.linkedTransactions ?? 0) > 0 &&
                            <Tooltip message="Cannot revert import with linked transactions."/>
                    }
                    <Button
                        onClick={() => setOpenDeleteModal(true)}
                        variant="contained"
                        disabled={!canRevert || (summary?.linkedTransactions ?? 0) > 0}
                        sx={{
                            bgcolor: importEvent?.isReverted ? 'var(--color-primary)' : 'red',
                            color: '#fff',
                            '&:hover': { bgcolor: importEvent?.isReverted ? 'var(--color-primary)' : 'red' },
                            textTransform: 'none',
                            ml: 2
                        }}
                    >
                        {importEvent?.isReverted ? 'Restore Import' : 'Revert Import'}
                    </Button>
                </Box>
            </Box>
        </>
    );

    return (
        <>
        <DeleteModal
            open={openDeleteModal}
            message={importEvent?.isReverted ? "Restore Import?" : "Revert Import?"}
            description={importEvent?.isReverted ? "This action will restore all transactions associated with this import." : "This action will delete all transactions associated with this import."}
            onCancel={() => setOpenDeleteModal(false)}
            onConfirm={() => {
                onRevert();
                setOpenDeleteModal(false);
            }}
        />
        {isMobile ? (
            <BottomSheetDialog open={open} onClose={onClose} heightVh={90}>
                <Box sx={{ p: 2, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {importSummaryContent}
                    {importSummaryFooter}
                </Box>
            </BottomSheetDialog>
        ) : (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    maxWidth: 1000,
                    width: '100%',
                    maxHeight: '80vh',
                    overflowY: 'hidden',
                    outline: 'none'
                }}
            >
                {importSummaryContent}
                {importSummaryFooter}
            </Box>
        </Modal>
        )}
        </>
    );
};

export default ImportSummaryPopup;